import {
  WSActionsProps,
  WSCard,
  WSDataItemProps,
  WSGrid,
  WSGridItemSpan,
  WSList,
  WSPanel,
  WSPillProps,
  WSSectionToolbar,
  WSSectionToolbarProps
} from "@wingspanhq/fe-component-library";
import { WSCardHeaderProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSCard/WSCardHeader";
import React, { useMemo } from "react";
import { DataItem } from "./DataItem";
import { WSGridProps } from "@wingspanhq/fe-component-library/dist/lib/components/layout/WSGrid/WSGrid.types";
import { WSCardProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSCard/WSCard";

export type DataViewProps = {
  title?: string;
  onEdit?: () => void;
  pill?: WSPillProps;
  header?: WSCardHeaderProps;
  actions?: WSActionsProps;
  divider?: WSCardProps["divider"];
} & DataViewGridProps;

export const DataView: React.FC<DataViewProps> = ({
  divider = true,
  header,
  loading,
  pill,
  title,
  onEdit,
  actions,
  data,
  fullWidth,
  children,
  ...otherProps
}) => {
  const headerView = useMemo(() => {
    if (header) {
      return header;
    }

    if (!title && !onEdit && !pill) {
      return;
    }

    if (onEdit || pill) {
      return {
        label: title,
        value: {
          pill: pill,
          action:
            !loading && onEdit
              ? {
                  label: "Edit",
                  onClick: onEdit
                }
              : undefined
        }
      };
    }

    return title;
  }, [header, onEdit, pill, title]);

  return (
    <WSCard divider={divider} header={headerView} actions={actions}>
      {data.length > 0 || children ? (
        <WSList gap="2XL">
          {data.length > 0 ? (
            <DataViewGrid
              data={data}
              loading={loading}
              fullWidth={fullWidth}
              {...otherProps}
            />
          ) : null}
          {children ? children : null}
        </WSList>
      ) : null}
    </WSCard>
  );
};

export interface DataItemProps extends WSDataItemProps {
  fullWidth?: boolean;
}

interface DataViewGridProps extends WSGridProps {
  data: (DataItemProps | React.ReactNode | null)[];
  fullWidth?: boolean;
  loading?: boolean;
  gridColumns?: number;
}

export const DataViewGrid: React.FC<DataViewGridProps> = ({
  data,
  fullWidth,
  loading,
  gridColumns = 2,
  ...otherProps
}) => (
  <WSGrid gutter="XL" {...otherProps}>
    {data.filter(Boolean).map((item, colIndex) => {
      if (!item) {
        return null;
      } else if (React.isValidElement(item)) {
        return (
          <WSGrid.Item shimmer={loading} key={String(colIndex)} span="12">
            {item}
          </WSGrid.Item>
        );
      } else if (typeof item === "object" && "label" in item) {
        const span = Math.floor(
          12 / gridColumns || 6
        ).toString() as WSGridItemSpan;

        return (
          <WSGrid.Item
            shimmer={loading}
            key={String(colIndex) + item.label}
            span={
              fullWidth || item.fullWidth
                ? "12"
                : {
                    m: span
                  }
            }
          >
            <DataItem {...item} />
          </WSGrid.Item>
        );
      }
    })}
  </WSGrid>
);

interface DataViewSectionProps extends WSSectionToolbarProps {
  data: (DataItemProps | React.ReactNode | null)[];
  fullWidth?: boolean;
  loading?: boolean;
  gridColumns?: number;
}

export const DataViewSection: React.FC<DataViewSectionProps> = ({
  data,
  fullWidth,
  loading,
  title,
  button,
  pill,
  tooltip,
  actions,
  hasBorder,
  children,
  gridColumns,
  ...marginProps
}) => {
  return (
    <WSList gap="S" {...marginProps}>
      <WSSectionToolbar
        title={title}
        button={button}
        pill={pill}
        tooltip={tooltip}
        actions={actions}
        hasBorder={hasBorder}
      />
      <WSPanel>
        <WSList gap="L">
          <DataViewGrid
            data={data}
            loading={loading}
            fullWidth={fullWidth}
            gridColumns={gridColumns}
          />
          {children}
        </WSList>
      </WSPanel>
    </WSList>
  );
};
